import { FC, useEffect, useState } from "react";
import { InfoBox } from "../../Elements/Alert/InfoBox";
import { useTranslation } from "react-i18next";
import { convertSurfaceToObj } from "../../utils/CalculateUtils";
import { Button, formatDate } from "@inceptionbg/ui-components";
import { IInputComponentProps, Input } from "../../Elements/Input";
import { SurfaceValue } from "../../Components/Inputs/Surface/SurfaceValue";
import { Section } from "../../Components/Section";
import { useLocation } from "react-router-dom";
import { IFarm } from "../../types/IFarm";
import { getFarmByRegNum, setFarmRequest } from "../../repos/FarmRepo";
import { parseUrlSearch } from "../../utils/UrlUtils";
import { Dialog } from "../../Elements/Dialog";
import { Loader } from "../../Elements/Loader";
import { IConsentClient } from "../../types/IConsent";
import { getConsentClient } from "../../repos/ConsentRepo";

const ConsentPage: FC = () => {
  const [farm, setFarm] = useState<IFarm | null>(null);

  const { t } = useTranslation();
  const { search } = useLocation();
  const { registrationNumber, requestDate, redirectUrl, state, clientId } =
    parseUrlSearch(search);

  const [isOpen, setIsOpen] = useState(false);

  const [errors, setErrors] = useState<string | null>(null);

  const [client, setClient] = useState<IConsentClient>();

  useEffect(() => {
    if(!!clientId) {
      getConsentClient(clientId).then((c) => {
        setClient(c);
      })
    }
  }, [clientId]);

  const loading = !client;

  const clientInfo: IInputComponentProps[] = [
    {
      label: t("ConsentOwner"),
      value: client?.ownerName,
    },
    {
      label: t("ConsentPurpose"),
      value: client?.purpose,
    }
  ];

  const providerInfo: IInputComponentProps[] = [

    {
      label: t("OrgNumberLong"),
      value: farm?.registrationNumber,
    },
    {
      label: t("HolderType"),
      value: t("HolderType" + farm?.holder.type!),
    },
    ...(farm?.holder.type === "physicalPerson"
      ? [
          {
            label: t("HolderFirstName"),
            value: farm.holder.firstName,
          },
          {
            label: t("HolderLastName"),
            value: farm.holder.lastName,
          },
        ]
      : []),
    ...(farm?.holder.type === "legalEntity"
      ? [
          {
            label: t("HolderTitle"),
            value: farm.holder.name,
          },
          {
            label: t("TaxId"),
            value: farm.holder.taxId,
          },
        ]
      : []),
    {
      label: t("HolderMunicipality"),
      value: farm?.headOffice?.municipality,
    },
    {
      label: t("Email"),
      value: farm?.contactInfo.email,
    },
    {
      label: t("ConsentRequestDate"),
      value: formatDate(requestDate!),
    },
    {
      label: t("UsedSurfaceConsent"),
      valueElement: (
        <SurfaceValue valueObj={convertSurfaceToObj(farm?.totalSurface)} />
      ),
    },
    {
      label: t("TotalCultureConvinientSurface"),
      valueElement: (
        <SurfaceValue valueObj={convertSurfaceToObj(farm?.totalGrantSurface)} />
      ),
    },
  ];

  useEffect(() => {
    if (registrationNumber && requestDate) {
      getFarmByRegNum(registrationNumber, requestDate)
        .then(setFarm)
        .catch((errors) => {
          if (errors.response.data.errors) {
            setErrors(
              errors.response.data.errors[0].errorCode || "AccessDeniedInfo"
            );
          }
        });
    }
  }, [registrationNumber, requestDate]);

  useEffect(() => {
    const timer = setTimeout(() => {
      window.location.href = redirectUrl + "?success=0"
    }, 600000);
  
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Loader isLoading={loading}>
      <div className="flex justify-center p-3">
        {/* TO DO -> Create reusable component for different providers (NIS, OMV...) */}
        {!loading && <Section id="consent" title={t("NisRequest")}>
          {!!farm ? (
            <>
              <div className="form-container mb-3">
                {clientInfo.map((e) => (
                  <Input key={e.label} component={e} edit={false}  />
                ))}
                <div style={{borderTop: '2px solid var(--primary)'}} />
                <b>{t('ConsentData')}</b>
                {providerInfo.map((e) => (
                  <Input key={e.label} component={e} edit={false} />
                ))}
              </div>
              <InfoBox title={t("NisRequestInfo", {purpose: client?.purpose, requester: client?.ownerName})} className="mb-3" />
              <div className="flex justify-center buttons-wrapper gap-2 mt-4">
                <Button
                  label={t("CancelRequest")}
                  size="s"
                  className="error"
                  outlined
                  onClick={() => {
                    if(clientId === 'nisagro') {
                      (window.location.href = "https://www.nisgazprom.rs/sr/programi-lojalnosti/nis-agro-kartica/")
                    } else {
                      (window.location.href = redirectUrl + "?success=0")
                    }
                  }}
                />
                <Button
                  label={t("SubmitRequest")}
                  primary
                  onClick={() =>
                    setFarmRequest({
                      registrationNumber,
                      atDay: requestDate,
                      state,
                      clientId
                    })
                      .then(() => setIsOpen(true))
                      .catch(() => setIsOpen(false))
                      .finally(() => {
                      })
                  }
                />
              </div>
              <Dialog
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                title={t("ConsentPageDialogTitle")}
                desc={t("ConsentPageDialogDescription")}
                confirmButton={{
                  onClick: () => {
                    window.location.href = redirectUrl + "?success=1";
                  },label:t('ConsetnPageDialogConfirmButton')
                }}
                notMobileFullScreen
                className="consent-page-dialog"
              />
            </>
          ) : (
            <InfoBox title={t("Error" + errors!)} className="mb-3 new-line" />
          )}
        </Section>}
      </div>
    </Loader>
  );
};

export default ConsentPage;
